/* eslint-disable max-statements */

import { Box, Button, Grid, Group, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { RecursivePartial, TransitConsignmentItem, TransitHouseConsignment } from '@e-origin/shared';

import { BackButton, SearchBox, Table } from '@components';
import { TransitViewHouseFormValues } from '../validateTransitViewHouseForm';
import { EditGoodsItem } from './EditGoodsItem';
import { useGoodsItemsDetailsColumns } from './useGoodsItemsDetailsColumns';

interface GoodsItemsDetailsProps {
  form: UseFormReturnType<TransitViewHouseFormValues>;
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
}

interface GoodsItemTableData {
  declarationGoodsItemNumber: string;
  goodsItemNumber: string;
  isMissing: boolean;
  receivedFromUnloadPermission: boolean;
  descriptionOfGoods: string;
  hsCode: string;
  grossMass: string;
  typeOfPackages: string;
  numberOfPackages: string;
  shippingMarks: string;
  houseConsignment: RecursivePartial<TransitHouseConsignment>;
  consignmentItem: RecursivePartial<TransitConsignmentItem>;
  consignmentItemIdx: number;
}

export const GoodsItemsDetails: React.FC<GoodsItemsDetailsProps> = ({ form, editDisabled, editDisabledConditionalForArrival }) => {
  const [tableData, setTableData] = useState([]);

  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const houseRef = queryParams.current.get('houseRef') ? Number(queryParams.current.get('houseRef')) : null;
  const navigate = useNavigate();

  const searchQuery = useRef<string>(queryParams.current.get('goodsSearch') ? queryParams.current.get('goodsSearch') : '');
  const selectedRow = useRef<GoodsItemTableData | null>(null);

  const buildTableData = () => {
    const data: GoodsItemTableData[] = [];

    const houseConsigments = houseRef !== null ? [form.values.Consignment.HouseConsignment[houseRef]] : form.values.Consignment?.HouseConsignment || [];

    for (const houseItem of houseConsigments) {
      for (const [consignmentItemIdx, consignmentItem] of (houseItem.ConsignmentItem || []).entries()) {
        if (
          !searchQuery.current ||
          consignmentItem.Commodity?.descriptionOfGoods?.toLowerCase().includes(searchQuery.current.toLowerCase()) ||
          consignmentItem.Commodity?.CommodityCode?.hsCode?.toLowerCase().includes(searchQuery.current.toLowerCase())
        ) {
          data.push({
            declarationGoodsItemNumber: consignmentItem.declarationGoodsItemNumber,
            goodsItemNumber: consignmentItem.goodsItemNumber,
            isMissing: consignmentItem.isMissing,
            receivedFromUnloadPermission: consignmentItem.receivedFromUnloadPermission,
            descriptionOfGoods: consignmentItem.Commodity?.descriptionOfGoods,
            hsCode: consignmentItem.Commodity?.CommodityCode?.hsCode,
            grossMass: consignmentItem.Commodity?.GoodsMeasure?.grossMass,
            typeOfPackages: consignmentItem.Packaging?.[0]?.typeOfPackages,
            numberOfPackages: consignmentItem.Packaging?.[0]?.numberOfPackages,
            shippingMarks: consignmentItem.Packaging?.[0]?.shippingMarks,
            houseConsignment: houseItem,
            consignmentItem,
            consignmentItemIdx,
          });
        }
      }
    }

    return data;
  };

  useEffect(() => {
    if (form.values && routeLocation) {
      setTableData(buildTableData());
    }
  }, [form.values, routeLocation]);

  const handleSave = (goodsItem) => {
    const { consignmentItemIdx = -1 } = selectedRow.current || {};

    if (consignmentItemIdx === -1) {
      form.setFieldValue(`Consignment.HouseConsignment.${houseRef}.ConsignmentItem`, [
        ...(form.values.Consignment.HouseConsignment[houseRef].ConsignmentItem || []),
        goodsItem,
      ]);
    } else {
      form.setFieldValue(`Consignment.HouseConsignment.${houseRef}.ConsignmentItem.${consignmentItemIdx}`, goodsItem);
    }
    selectedRow.current = null;
  };

  const handleSearch = (value: string) => {
    searchQuery.current = value;
    setTableData(buildTableData());

    queryParams.current.set('goodsSearch', value);
    navigate({ search: queryParams.current.toString() });
  };

  const openGoodsItem = (row?) => {
    modals.open({
      title: `${!row ? 'Create' : 'Update'} Goods Item`,
      children: (
        <EditGoodsItem
          form={form}
          onSave={handleSave}
          onHide={modals.closeAll}
          houseConsignmentIdx={houseRef}
          goodsItemIdx={row?.consignmentItemIdx}
          editDisabled={editDisabled}
          editDisabledConditionalForArrival={editDisabledConditionalForArrival}
        />
      ),
      size: '60vw',
    });
  };

  const { columns } = useGoodsItemsDetailsColumns({
    form,
    houseRef,
    openGoodsItem,
    editDisabled,
    editDisabledConditionalForArrival,
    selectedRow,
  });

  return (
    <>
      <BackButton backPath={routeLocation.pathname} data-pw="transit-goods-items-back-button"></BackButton>
      <Grid justify="space-between" align="center">
        <Grid.Col span={4}>
          <Title order={5} fw={600}>
            Goods items {form?.values?.Consignment.HouseConsignment[houseRef]?.Consignor?.name}
          </Title>
        </Grid.Col>

        <Grid.Col span={6}>
          <Group display={'flex'} py={12} justify="end" gap={10}>
            <SearchBox handleSearch={handleSearch} value={searchQuery.current} placeholder="Search goods by HS code, description of goods" />
            {(!editDisabled || !editDisabledConditionalForArrival) && (
              <Button onClick={() => openGoodsItem()} variant="outline" size="lg" data-pw="add-goods-item-btn">
                Add new
              </Button>
            )}
          </Group>
        </Grid.Col>
      </Grid>
      <Box mb={20}>
        <Table key={searchQuery.current} columns={columns} dataSource={tableData || []} pagination verticalSpacing="xs" />
      </Box>
    </>
  );
};
